import { useState } from "react";
import { Loader2, Inbox, AlertTriangle } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "./ui/alert-dialog";

const TONES = {
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  red: "bg-red-50 text-red-700 border-red-200",
  blue: "bg-[#EEF3FD] text-[#245BDB] border-[#D5E1FA]",
  gray: "bg-[#F6F8FC] text-[#475569] border-[#E2E8F0]",
  navy: "bg-[#14213D] text-white border-[#14213D]",
};
const STATUS_TONE = {
  active: "green", ready: "green", sent: "green", delivered: "green", verified: "green", succeeded: "green", connected: "green", resolved: "gray", received: "blue",
  queued: "gray", pending: "amber", crawling: "blue", running: "blue", retrying: "amber", draft: "gray", approval: "amber", paused: "amber", open: "blue",
  failed: "red", bounced: "red", complained: "red", rejected: "red", escalated: "red", high: "red", urgent: "red", not_configured: "amber", provider_required: "amber",
  dns_required: "amber", awaiting_first_email: "amber", not_started: "gray", coming_soon: "gray", planned: "gray", available: "blue", revoked: "red", auto: "blue",
  superseded: "gray", sending: "blue", failing: "amber", indexed: "green", demo: "amber",
};

export function Badge({ tone = "gray", children, testid, className = "" }) {
  return <span data-testid={testid} className={`chip ${TONES[tone]} ${className}`}>{children}</span>;
}

export function Status({ value, testid }) {
  const tone = STATUS_TONE[value] || "gray";
  return (
    <Badge tone={tone} testid={testid}>
      <span className={`h-1.5 w-1.5 rounded-full ${tone === "green" ? "bg-emerald-500" : tone === "red" ? "bg-red-500" : tone === "amber" ? "bg-amber-500" : tone === "blue" ? "bg-[#3974E8]" : "bg-slate-400"} ${["crawling", "running", "sending"].includes(value) ? "pulse-dot" : ""}`} />
      {String(value || "unknown").replace(/_/g, " ")}
    </Badge>
  );
}

export function PageHeader({ title, description, actions, eyebrow }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
      <div>
        {eyebrow && <div className="eyebrow mb-1.5">{eyebrow}</div>}
        <h1 className="text-[22px] font-bold text-[#111827]" data-testid="page-title">{title}</h1>
        {description && <p className="text-sm muted mt-1 max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, icon: Icon, testid }) {
  return (
    <div className="card p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-xs muted font-medium">{label}{Icon && <Icon size={14} className="text-[#94A3B8]" />}</div>
      <div className="mt-2 text-2xl font-display font-bold text-[#111827] tabular-nums">{value ?? "—"}</div>
      {hint && <div className="text-[11px] muted mt-1">{hint}</div>}
    </div>
  );
}

export function Empty({ icon: Icon = Inbox, title, body, action, testid = "empty-state" }) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-14 px-6" data-testid={testid}>
      <div className="h-10 w-10 rounded-lg border border-[#E2E8F0] bg-[#F6F8FC] grid place-items-center mb-3"><Icon size={18} className="text-[#64748B]" /></div>
      <div className="font-semibold text-sm text-[#111827]">{title}</div>
      {body && <p className="text-sm muted mt-1 max-w-sm">{body}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function Loading({ rows = 4 }) {
  return (
    <div className="space-y-2 p-4" data-testid="loading-state">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-9 rounded bg-[#F1F4FA] animate-pulse" />)}
    </div>
  );
}

export function ErrorBox({ error, retry }) {
  return (
    <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="error-state">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <div className="flex-1">{error}</div>
      {retry && <button className="btn-sm btn-secondary" onClick={retry}>Retry</button>}
    </div>
  );
}

export function DataState({ q, empty, children, rows }) {
  if (q.loading && !q.data) return <Loading rows={rows} />;
  if (q.error) return <div className="p-4"><ErrorBox error={q.error} retry={q.reload} /></div>;
  if (empty) return empty;
  return children;
}

export function Spinner({ size = 14 }) { return <Loader2 size={size} className="animate-spin" />; }

export function Confirm({ trigger, title, body, onConfirm, confirmLabel = "Confirm", testid }) {
  const [busy, setBusy] = useState(false);
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent data-testid={testid}>
        <AlertDialogHeader><AlertDialogTitle>{title}</AlertDialogTitle><AlertDialogDescription>{body}</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction data-testid="confirm-ok" className="bg-red-600 hover:bg-red-700" onClick={async () => { setBusy(true); try { await onConfirm(); } finally { setBusy(false); } }}>
            {busy && <Spinner />}{confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export function Field({ label, children, hint }) {
  return <label className="block"><span className="label">{label}</span>{children}{hint && <span className="block text-[11px] muted mt-1">{hint}</span>}</label>;
}

export function Card({ title, actions, children, className = "", bodyClass = "p-4", testid }) {
  return (
    <section className={`card ${className}`} data-testid={testid}>
      {title && <header className="flex items-center justify-between px-4 h-11 border-b border-[#EEF2F7]"><h3 className="text-[13px] font-semibold text-[#111827]">{title}</h3>{actions}</header>}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return (
    <div className="flex gap-1 border-b border-[#E2E8F0] mb-5 overflow-x-auto">
      {tabs.map((t) => (
        <button key={t} data-testid={`tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => onChange(t)}
          className={`px-3 h-9 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${value === t ? "border-[#245BDB] text-[#111827] font-medium" : "border-transparent muted hover:text-[#111827]"}`}>{t}</button>
      ))}
    </div>
  );
}

export function Pager({ page, pages, onPage }) {
  if (!pages || pages <= 1) return null;
  return (
    <div className="flex items-center justify-between px-4 py-3 text-xs muted" data-testid="pager">
      <span>Page {page} of {pages}</span>
      <div className="flex gap-2">
        <button className="btn-secondary btn-sm" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">Previous</button>
        <button className="btn-secondary btn-sm" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next">Next</button>
      </div>
    </div>
  );
}

export function CodeBlock({ code, lang = "bash" }) {
  return (
    <div className="rounded-lg bg-[#0E1830] border border-[#1E2B4A] overflow-hidden">
      <div className="flex items-center justify-between px-3 h-8 border-b border-[#1E2B4A] text-[11px] text-[#8A9BC0] mono"><span>{lang}</span>
        <button className="hover:text-white" onClick={() => navigator.clipboard?.writeText(code)} data-testid="code-copy-btn">copy</button></div>
      <pre className="p-4 text-[12.5px] leading-relaxed text-[#D6E0F5] overflow-x-auto mono">{code}</pre>
    </div>
  );
}
